import { Button } from "antd";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, Crosshair, Download } from "react-feather";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { IonSpinner } from "@ionic/react";
import DOMPurify from "dompurify";
import { rgbToHex2 } from "../helpers/helpers";
import { useAuth } from "../../../contexts/AuthContext";
import { isNativeiOS } from "../../../lib/utils/platform";
import { isEmbroideryTrace } from "../../../config/appVariant";
import IOSPaywall from "../../PaywallModal/IOSPaywall";
import { subsampleStitches } from "../../SvgEditor/components/stitchParser";
import ThreadSeparationPreview from "./ThreadSeparationPreview";

// Renders a live DST/embroidery stitch preview on a canvas element
const StitchPreviewCanvas = ({ stitchData, layerColors, style }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !stitchData?.stitches?.length) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || 480;
    const h = rect.height || 480;
    canvas.width = w * dpr;
    canvas.height = h * dpr;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Linen-white background so thread colors read clearly
    ctx.fillStyle = "#faf8f5";
    ctx.fillRect(0, 0, w, h);

    const { minX, maxX, minY, maxY, stitches, colors: stitchColors } = stitchData;
    const dataW = (maxX - minX) || 1;
    const dataH = (maxY - minY) || 1;
    const pad = 24;
    const areaW = w - pad * 2;
    const areaH = h - pad * 2;
    const scale = Math.min(areaW / dataW, areaH / dataH);
    const offX = pad + (areaW - dataW * scale) / 2;
    const offY = pad + (areaH - dataH * scale) / 2;

    const toX = (x) => offX + (x - minX) * scale;
    const toY = (y) => offY + (y - minY) * scale;

    // Use user's layer colors if available, else fall back to DST palette
    const palette = layerColors?.length ? layerColors : (stitchColors || ["#333"]);
    let lastColorIdx = -1;

    ctx.lineWidth = 1.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const sampled = subsampleStitches(stitches, 40000);
    for (let i = 0; i < sampled.length; i++) {
      const s = sampled[i];
      if (s.colorIndex !== lastColorIdx) {
        if (lastColorIdx !== -1) ctx.stroke();
        lastColorIdx = s.colorIndex;
        ctx.beginPath();
        ctx.strokeStyle = palette[s.colorIndex % palette.length] || "#333";
        ctx.moveTo(toX(s.x), toY(s.y));
        continue;
      }
      if (s.jump) {
        ctx.moveTo(toX(s.x), toY(s.y));
      } else {
        ctx.lineTo(toX(s.x), toY(s.y));
      }
    }
    ctx.stroke();
  }, [stitchData, layerColors]);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block", ...style }} />;
};

// Bottom bar inside the stitch preview: stitch count + Download .DST button
const DstDownloadBar = ({ rawBase64, stitchCount }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!rawBase64 || downloading) return;
    setDownloading(true);
    try {
      const bin = atob(rawBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "embroidery.dst";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("[DST Download]", e);
    } finally {
      setDownloading(false);
    }
  };

  const formatted = stitchCount != null ? stitchCount.toLocaleString() : null;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 12px",
      borderTop: "1px solid rgba(0,0,0,0.06)",
      background: "#fff",
      flexShrink: 0,
    }}>
      {/* Stitch count */}
      {formatted && (
        <span style={{
          fontFamily: "Clash Grotesk",
          fontSize: "11px",
          color: "#64748b",
          fontWeight: 500,
          whiteSpace: "nowrap",
          flex: 1,
        }}>
          {formatted} stitches
        </span>
      )}

      {/* Download button */}
      <button
        onClick={handleDownload}
        disabled={downloading}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: downloading ? "#e2e8f0" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
          color: downloading ? "#94a3b8" : "#fff",
          border: "none",
          borderRadius: "8px",
          padding: "7px 14px",
          fontFamily: "Clash Grotesk",
          fontSize: "12px",
          fontWeight: 700,
          cursor: downloading ? "not-allowed" : "pointer",
          transition: "all 0.15s",
          whiteSpace: "nowrap",
          boxShadow: downloading ? "none" : "0 2px 8px rgba(22,163,74,0.3)",
          letterSpacing: "0.2px",
        }}
        onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.filter = "brightness(1.08)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.filter = ""; }}
      >
        {downloading ? (
          <IonSpinner name="crescent" style={{ width: "12px", height: "12px" }} />
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        )}
        Download .DST
      </button>
    </div>
  );
};

// Watermark overlay for guest mode - prevents screenshots
const PreviewWatermark = () => (
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: "none",
      zIndex: 50,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: "-50%",
        left: "-50%",
        width: "200%",
        height: "200%",
        transform: "rotate(-30deg)",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, 220px)",
        gridTemplateRows: "repeat(auto-fill, 80px)",
        alignItems: "center",
        justifyItems: "center",
      }}
    >
      {Array.from({ length: 120 }).map((_, i) => (
        <span
          key={i}
          style={{
            fontSize: "28px",
            fontWeight: "800",
            color: "rgba(0, 0, 0, 0.12)",
            fontFamily: "Clash Grotesk, sans-serif",
            letterSpacing: "5px",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            userSelect: "none",
          }}
        >
          PREVIEW
        </span>
      ))}
    </div>
  </div>
);

const zoomBtnStyle = {
  background: "rgba(255,255,255,0.92)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "none",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
  color: "#64748b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const SvgZoomPreview = ({ svgRaw, svgPreview, svgRef: externalRef, borderRadius = "6px", border, boxShadow = "0 1px 4px rgba(0,0,0,0.06)" }) => {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const pinchRef = useRef({ active: false, startDist: 0, startScale: 1 });
  const panRef = useRef({ active: false, startX: 0, startY: 0, startTx: 0, startTy: 0 });

  const clampScale = (s) => Math.min(Math.max(s, 0.25), 10);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { active: true, startDist: Math.hypot(dx, dy), startScale: scale };
      panRef.current.active = false;
    } else if (e.touches.length === 1) {
      panRef.current = { active: true, startX: e.touches[0].clientX, startY: e.touches[0].clientY, startTx: translate.x, startTy: translate.y };
    }
  };

  const handleTouchMove = (e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const newScale = clampScale(pinchRef.current.startScale * (dist / pinchRef.current.startDist));
      setScale(newScale);
    } else if (panRef.current.active && e.touches.length === 1 && scale > 1) {
      const dx = e.touches[0].clientX - panRef.current.startX;
      const dy = e.touches[0].clientY - panRef.current.startY;
      setTranslate({ x: panRef.current.startTx + dx, y: panRef.current.startTy + dy });
    }
  };

  const handleTouchEnd = () => {
    pinchRef.current.active = false;
    panRef.current.active = false;
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((s) => clampScale(s * delta));
  };

  const resetView = () => { setScale(1); setTranslate({ x: 0, y: 0 }); };

  const cleanSvg = svgRaw ? DOMPurify.sanitize(svgRaw.replace(/<svg([^>]*)>/, (match, attrs) => {
    const cleaned = attrs.replace(/\s(width|height)="[^"]*"/g, '');
    return `<svg${cleaned}>`;
  }), { USE_PROFILES: { svg: true, svgFilters: true } }) : null;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        touchAction: "none",
      }}
    >
      <div style={{
        transform: `translate(${translate.x}px, ${translate.y}px)`,
        width: `${scale * 90}%`,
        maxHeight: "90%",
        transition: pinchRef.current.active ? "none" : "width 0.15s ease-out",
        flexShrink: 0,
      }}>
        {isEmbroideryTrace && (svgRaw || svgPreview) ? (
          <div ref={externalRef} data-tutorial="svg-content">
            <ThreadSeparationPreview svgRaw={svgRaw} svgPreview={svgPreview} />
          </div>
        ) : cleanSvg ? (
          <div
            ref={externalRef}
            data-tutorial="svg-content"
            dangerouslySetInnerHTML={{ __html: cleanSvg }}
            className="svg-inline-preview"
            style={{
              width: "100%",
              borderRadius,
              border: border || undefined,
              backgroundColor: "transparent",
              boxShadow,
              lineHeight: 0,
              overflow: "hidden",
            }}
          />
        ) : svgPreview ? (
          <img
            ref={externalRef}
            data-tutorial="svg-content"
            src={svgPreview}
            alt="SVG Preview"
            style={{
              width: "100%",
              objectFit: "contain",
              borderRadius,
              border: border || undefined,
              backgroundColor: "transparent",
              boxShadow,
            }}
          />
        ) : null}
      </div>

      {/* Zoom controls */}
      <div style={{ position: "absolute", top: "8px", right: "8px", display: "flex", gap: "4px", zIndex: 10 }}>
        <Button shape="circle" icon={<ZoomIn size={14} style={{ display: "block" }} />} size="small"
          onClick={() => setScale((s) => clampScale(s * 1.3))} title="Zoom in" style={zoomBtnStyle} />
        <Button shape="circle" icon={<ZoomOut size={14} style={{ display: "block" }} />} size="small"
          onClick={() => setScale((s) => clampScale(s / 1.3))} title="Zoom out" style={zoomBtnStyle} />
        <Button shape="circle" icon={<Crosshair size={14} style={{ display: "block" }} />} size="small"
          onClick={resetView} title="Recenter" style={zoomBtnStyle} />
      </div>
    </div>
  );
};

const SplitCanvas = (props) => {
  const [compHeight, setCompHeight] = React.useState("");
  const [initialTouchX, setInitialTouchX] = React.useState(null);
  const [initialTouchY, setInitialTouchY] = React.useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showIOSPaywall, setShowIOSPaywall] = useState(false);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const { userIsPremium } = useAuth();

  // Synced zoom refs (used in default/split mode)
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const syncingRef = useRef(false);

  // Measure the split container for fixed-pixel content sizing
  const splitContainerRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(0);

  useEffect(() => {
    const el = splitContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setPanelWidth(Math.floor(entries[0].contentRect.width / 2));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-center both panels after the content box size is known
  useEffect(() => {
    if (panelWidth > 0) {
      setTimeout(() => {
        syncingRef.current = true;
        if (leftRef.current) leftRef.current.centerView(1, 0);
        if (rightRef.current) rightRef.current.centerView(1, 0);
        requestAnimationFrame(() => { syncingRef.current = false; });
      }, 80);
    }
  }, [panelWidth]);

  const handleLeftTransformed = useCallback((ref, state) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (rightRef.current) {
      rightRef.current.setTransform(state.positionX, state.positionY, state.scale, 0);
    }
    requestAnimationFrame(() => { syncingRef.current = false; });
  }, []);

  const handleRightTransformed = useCallback((ref, state) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (leftRef.current) {
      leftRef.current.setTransform(state.positionX, state.positionY, state.scale, 0);
    }
    requestAnimationFrame(() => { syncingRef.current = false; });
  }, []);

  // Download handler for the original SVG
  const handleDownloadOriginal = async () => {
    if (!props.downloadSVGLink || props.previewLoading) return;

    // On iOS: require premium to export SVG
    if (isNativeiOS() && !userIsPremium) {
      setShowIOSPaywall(true);
      return;
    }
    
    setIsDownloading(true);
    
    try {
      // Save to cloud first
      if (props.saveToCloud) {
        await props.saveToCloud(false); // false = don't open editor
      }
      
      // Then download the SVG
      const link = document.createElement("a");
      link.href = props.downloadSVGLink;
      link.download = "converted.svg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading SVG:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    // Setup raster image canvas at full resolution
    setTimeout(() => {
      if (props.file) {
        const img = new Image();

        img.onload = function () {
          const canvas = canvasRef.current;
          if (canvas) {
            // Set canvas to actual image dimensions for full resolution
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Draw image at full resolution
            canvas
              .getContext("2d")
              .drawImage(
                img,
                0,
                0,
                img.width,
                img.height,
                0,
                0,
                img.width,
                img.height
              );

            // Re-center the left panel now that the canvas has real dimensions.
            // Suppress sync so this doesn't bleed into the right panel.
            setTimeout(() => {
              syncingRef.current = true;
              if (leftRef.current) leftRef.current.centerView(1, 0);
              requestAnimationFrame(() => { syncingRef.current = false; });
            }, 50);
          }
        };

        img.src = `${props.file}`;
      }
    }, 300);
  }, [compHeight, props.file]);

  // Re-center the right panel when SVG content arrives or changes.
  // Suppress the sync so the right panel's "centered for SVG" transform
  // doesn't get copied to the left panel (which has different content dimensions).
  useEffect(() => {
    if (props.svgPreview || props.svgRaw) {
      setTimeout(() => {
        syncingRef.current = true;
        if (rightRef.current) rightRef.current.centerView(1, 0);
        requestAnimationFrame(() => { syncingRef.current = false; });
      }, 100);
    }
  }, [props.svgPreview, props.svgRaw]);

  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      setInitialTouchX(touch.pageX);
      setInitialTouchY(touch.pageY);
    }
  };

  const copyColor = (e) => {
    e.preventDefault();
    
    // Check for drag on touch events
    if (e.type === "touchend") {
      if (e.changedTouches && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        const deltaX = Math.abs(touch.pageX - initialTouchX);
        const deltaY = Math.abs(touch.pageY - initialTouchY);
        
        if (deltaX > 5 || deltaY > 5) {
          return;
        }
      }
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    // Use the appropriate properties for mouse and touch events
    const clientX = e.type === "touchend" ? e.changedTouches[0].clientX : e.clientX;
    const clientY = e.type === "touchend" ? e.changedTouches[0].clientY : e.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    if (x >= 0 && x < canvas.width && y >= 0 && y < canvas.height) {
      const pixelData = ctx.getImageData(x, y, 1, 1).data;
      const hex = (
        "000000" + rgbToHex2(pixelData[0], pixelData[1], pixelData[2])
      ).slice(-6);
      
      if (props.setClickedColor) {
        props.setClickedColor(hex);
      }
    }
  };

  const handleMouseMove = (e) => {
    // Optional: Add hover effects or color preview
  };

  useEffect(() => {
    const handleResize = () => {
      setCompHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // If singleColumn mode, render only one side
  if (props.singleColumn) {
    if (props.showOriginal) {
      // Render only the original image
      return (
        <div
          data-tutorial="original-image"
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            backgroundColor: "#f1f3f5",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: props.setClickedColor ? "crosshair" : "default",
          }}
        >
          <div
            id="splitCanvasContainer"
            data-tutorial="original-image-content"
            style={{
              overflow: "hidden",
              backgroundColor: "#f1f3f5",
              height: "100%",
              position: "relative",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              touchAction: "none",
              overscrollBehavior: "contain",
            }}
          >
            <TransformWrapper minScale={0.25} initialScale={1} limitToBounds={false} centerOnInit={false}>
              {({ zoomIn, zoomOut, centerView }) => (
                <>
                  <TransformComponent wrapperStyle={{ overflow: "visible", touchAction: "none" }}>
                    <canvas
                      ref={canvasRef}
                      id="canvas"
                      data-tutorial="original-image-only"
                      width="10"
                      height="10"
                      onTouchStart={handleTouchStart}
                      onTouchEnd={copyColor}
                      onMouseUp={copyColor}
                      onMouseMove={handleMouseMove}
                      style={{
                        display: "block",
                        margin: "auto",
                        maxWidth: "90%",
                        maxHeight: "90%",
                        objectFit: "contain",
                        cursor: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewbox="0 0 48 48" ><path stroke-width="1" style="stroke: white; fill: black;" d="M17.542 13.147l-7.455-6.667 1.334-1.49c.822.736 2.087.666 2.822-.159l3.503-3.831c.593-.663 1.414-1 2.238-1 1.666 0 3.016 1.358 3.016 2.996 0 .723-.271 1.435-.78 2.005l-3.503 3.83c-.735.824-.666 2.087.158 2.825l-1.333 1.491zm-4.314-1.175l-7.791 8.65c-.314.352-1.322.323-1.853.557.172-.554.048-1.538.362-1.89l7.791-8.651-1.491-1.333-7.9 8.794c-1.277 1.423-.171 2.261-1.149 4.052-.135.244-.197.48-.197.698 0 .661.54 1.151 1.141 1.141.241 0 .492-.079.724-.256 1.733-1.332 2.644-.184 3.954-1.647l7.9-8.792-1.491-1.333z"/></svg>') 0 24, auto`,
                      }}
                    />
                  </TransformComponent>
                  
                  {/* Zoom controls — bottom-left to avoid overlapping floating controls */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "16px",
                      left: "16px",
                      display: "flex",
                      gap: "6px",
                      zIndex: 10,
                    }}
                  >
                    <Button shape="circle" icon={<ZoomIn size={14} style={{ display: "block" }} />} size="small" onClick={() => zoomIn()} title="Zoom in" style={zoomBtnStyle} />
                    <Button shape="circle" icon={<ZoomOut size={14} style={{ display: "block" }} />} size="small" onClick={() => zoomOut()} title="Zoom out" style={zoomBtnStyle} />
                    <Button shape="circle" icon={<Crosshair size={14} style={{ display: "block" }} />} size="small" onClick={() => centerView(1)} title="Recenter" style={zoomBtnStyle} />
                    
                    {/* Download Original Button - hidden in guest mode */}
                    {!props.guestMode && props.downloadSVGLink && (
                      <Button
                        shape="circle"
                        icon={isDownloading ? <IonSpinner name="dots" style={{ width: "14px", height: "14px" }} /> : <Download size={14} style={{ display: "block" }} />}
                        size="small"
                        onClick={handleDownloadOriginal}
                        disabled={!props.downloadSVGLink || props.previewLoading || isDownloading}
                        data-tutorial="download-button"
                        title="Download SVG"
                        style={{
                          ...zoomBtnStyle,
                          opacity: (!props.downloadSVGLink || props.previewLoading || isDownloading) ? 0.5 : 1,
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </TransformWrapper>
          </div>
        </div>
      );
    } else {
      // Render only the SVG preview
      return (
        <div
          data-tutorial="svg-preview"
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            backgroundColor: "#f1f3f5",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          {props.previewLoading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(248, 248, 248, 0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 100,
                backdropFilter: "blur(4px)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    border: "3px solid #e2e8f0",
                    borderTop: "3px solid #e83e8c",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <span
                  style={{
                    fontFamily: "Clash Grotesk",
                    fontSize: "12px",
                    color: "#94a3b8",
                    fontWeight: 500,
                  }}
                >
                  Generating SVG...
                </span>
              </div>
            </div>
          )}
          
          {props.svgPreview ? (
            <div style={{ position: "relative", overflow: "hidden", borderRadius: "6px" }}>
              <SvgZoomPreview
                svgRaw={props.svgRaw}
                svgPreview={props.svgPreview}
                svgRef={svgRef}
                borderRadius="6px"
                boxShadow="0 1px 4px rgba(0,0,0,0.06)"
              />
              {props.guestMode && <PreviewWatermark />}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#cbd5e1",
                fontSize: "13px",
                textAlign: "center",
                padding: "20px",
                fontFamily: "Clash Grotesk",
              }}
            >
              {props.file ? (
                <>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      border: "3px solid #e2e8f0",
                      borderTop: "3px solid #e83e8c",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginBottom: "14px",
                    }}
                  />
                  <div style={{ fontWeight: 500, color: "#64748b", marginBottom: "6px", fontSize: "14px" }}>
                    Converting to SVG...
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", maxWidth: "200px", lineHeight: "1.4" }}>
                    Tracing colors and generating vector paths
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.6 }}>📄</div>
                  <div style={{ fontWeight: 500, color: "#94a3b8", marginBottom: "4px" }}>
                    SVG preview will appear here
                  </div>
                  <div style={{ fontSize: "11px", color: "#cbd5e1" }}>
                    Add colors to generate
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      );
    }
  }

  const contentBoxPx = Math.floor(panelWidth * 0.8);

  // Default: render both sides with synchronized zoom
  const syncedZoomIn = () => {
    if (leftRef.current) leftRef.current.zoomIn(0.5, 200);
    if (rightRef.current) rightRef.current.zoomIn(0.5, 200);
  };
  const syncedZoomOut = () => {
    if (leftRef.current) leftRef.current.zoomOut(0.5, 200);
    if (rightRef.current) rightRef.current.zoomOut(0.5, 200);
  };
  const syncedReset = () => {
    if (leftRef.current) leftRef.current.centerView(1, 200);
    if (rightRef.current) rightRef.current.centerView(1, 200);
  };

  const cleanSvgForSplit = props.svgRaw ? DOMPurify.sanitize(props.svgRaw.replace(/<svg([^>]*)>/, (match, attrs) => {
    const cleaned = attrs.replace(/\s(width|height)="[^"]*"/g, '');
    return `<svg${cleaned}>`;
  }), { USE_PROFILES: { svg: true, svgFilters: true } }) : null;

  return (
    <>
    <div
      ref={splitContainerRef}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        position: "relative",
      }}
    >
      {/* Left Half - Raster Image */}
      <div
        data-tutorial="original-image"
        style={{
          width: "50%",
          height: "100%",
          overflow: "hidden",
          backgroundColor: "#f1f3f5",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRight: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "6px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "10px",
            fontWeight: "500",
            color: "rgba(0,0,0,0.35)",
            backgroundColor: "transparent",
            padding: "2px 8px",
            zIndex: 10,
            fontFamily: "Clash Grotesk",
            letterSpacing: "0.2px",
            pointerEvents: "none",
          }}
        >
          {isEmbroideryTrace ? "Artwork · pick thread colors here" : "Original image · pick colors here"}
        </div>
        
        <div
          id="splitCanvasContainer"
          data-tutorial="original-image-content"
          style={{
            overflow: "hidden",
            backgroundColor: "#f1f3f5",
            height: "100%",
            position: "relative",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            touchAction: "none",
          }}
        >
          <TransformWrapper
            ref={leftRef}
            minScale={0.25}
            maxScale={10}
            initialScale={1}
            limitToBounds={false}
            centerOnInit={true}
            onTransformed={handleLeftTransformed}
            panning={{ velocityDisabled: true }}
          >
            {() => (
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%", overflow: "hidden" }}
              >
                {contentBoxPx > 0 && (
                  <div style={{ width: contentBoxPx + "px" }}>
                    <canvas
                      ref={canvasRef}
                      id="canvas"
                      data-tutorial="original-image-only"
                      width="10"
                      height="10"
                      onTouchStart={handleTouchStart}
                      onTouchEnd={copyColor}
                      onMouseUp={copyColor}
                      onMouseMove={handleMouseMove}
                      style={{
                        display: "block",
                        width: "100%",
                        height: "auto",
                        cursor: props.setClickedColor
                          ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewbox="0 0 48 48" ><path stroke-width="1" style="stroke: white; fill: black;" d="M17.542 13.147l-7.455-6.667 1.334-1.49c.822.736 2.087.666 2.822-.159l3.503-3.831c.593-.663 1.414-1 2.238-1 1.666 0 3.016 1.358 3.016 2.996 0 .723-.271 1.435-.78 2.005l-3.503 3.83c-.735.824-.666 2.087.158 2.825l-1.333 1.491zm-4.314-1.175l-7.791 8.65c-.314.352-1.322.323-1.853.557.172-.554.048-1.538.362-1.89l7.791-8.651-1.491-1.333-7.9 8.794c-1.277 1.423-.171 2.261-1.149 4.052-.135.244-.197.48-.197.698 0 .661.54 1.151 1.141 1.141.241 0 .492-.079.724-.256 1.733-1.332 2.644-.184 3.954-1.647l7.9-8.792-1.491-1.333z"/></svg>') 0 24, auto`
                          : "default",
                      }}
                    />
                  </div>
                )}
              </TransformComponent>
            )}
          </TransformWrapper>
        </div>
      </div>

      {/* Right Half - traced SVG preview. Stitch analysis begins in the editor. */}
      <div
        data-tutorial="svg-preview"
        style={{
          width: "50%",
          height: "100%",
          overflow: "hidden",
          backgroundColor: isEmbroideryTrace && (props.dstPreviewData || props.isDstGenerating) ? "#faf8f5" : "#f1f3f5",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Panel label */}
        <div
          style={{
            position: "absolute",
            top: "6px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "10px",
            fontWeight: "500",
            color: "rgba(0,0,0,0.35)",
            backgroundColor: "transparent",
            padding: "2px 8px",
            zIndex: 10,
            fontFamily: "Clash Grotesk",
            letterSpacing: "0.2px",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            whiteSpace: "nowrap",
          }}
        >
          {isEmbroideryTrace ? "Stitch rendering" : "Vector preview"}
          {isEmbroideryTrace && props.isDstGenerating && (
            <IonSpinner name="crescent" style={{ width: "10px", height: "10px", color: "#e83e8c" }} />
          )}
        </div>

        {/* Embroidery stitch canvas — shown when DST data is ready */}
        {isEmbroideryTrace && props.dstPreviewData ? (
          <div style={{ width: "100%", height: "100%", position: "relative", display: "flex", flexDirection: "column" }}>
            {/* Stitch canvas — fills all space above the download bar */}
            <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
              <StitchPreviewCanvas
                stitchData={props.dstPreviewData}
                layerColors={props.colors?.map(([hex]) => `#${hex}`)}
                style={{ borderRadius: "0px" }}
              />
              {props.guestMode && <PreviewWatermark />}
              {/* Regenerating overlay — shown when SVG changed and new DST is in-flight */}
              {props.isDstGenerating && (
                <div style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(250,248,245,0.72)",
                  backdropFilter: "blur(3px)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  pointerEvents: "none",
                }}>
                  <IonSpinner name="crescent" style={{ width: "22px", height: "22px", color: "#e83e8c" }} />
                  <span style={{ fontFamily: "Clash Grotesk", fontSize: "11px", color: "#94a3b8", fontWeight: 500 }}>
                    {props.dstStatus || "Updating stitch preview…"}
                  </span>
                </div>
              )}
            </div>

          </div>
        ) : isEmbroideryTrace && props.isDstGenerating ? (
          /* Initial DST generation loading state (no previous data yet) */
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            fontFamily: "Clash Grotesk",
          }}>
            <div style={{
              width: "36px",
              height: "36px",
              border: "3px solid #e2e8f0",
              borderTop: "3px solid #e83e8c",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }} />
            <div style={{ fontWeight: 500, color: "#64748b", fontSize: "14px" }}>
              {props.dstStatus || "Generating stitch preview…"}
            </div>
            <div style={{ fontSize: "11px", color: "#94a3b8", maxWidth: "200px", lineHeight: "1.5", textAlign: "center" }}>
              {props.dstStatus === "Analysing design with AI…"
                ? "Optimising stitch type and density per layer"
                : "Converting SVG to embroidery stitches"}
            </div>
          </div>
        ) : (cleanSvgForSplit || props.svgPreview) ? (
          /* SVG preview — shown for non-embroidery or while waiting for first DST */
          <div style={{ width: "100%", height: "100%", overflow: "hidden", touchAction: "none" }}>
            <TransformWrapper
              ref={rightRef}
              minScale={0.25}
              maxScale={10}
              initialScale={1}
              limitToBounds={false}
              centerOnInit={true}
              onTransformed={handleRightTransformed}
              panning={{ velocityDisabled: true }}
            >
              {() => (
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%", overflow: "hidden" }}
                >
                  {contentBoxPx > 0 && (
                    <div style={{ width: contentBoxPx + "px", position: "relative", overflow: "hidden", borderRadius: "4px" }}>
                      {isEmbroideryTrace ? (
                        <div ref={svgRef} data-tutorial="svg-content">
                          <ThreadSeparationPreview svgRaw={props.svgRaw} svgPreview={props.svgPreview} />
                        </div>
                      ) : cleanSvgForSplit ? (
                        <div
                          ref={svgRef}
                          data-tutorial="svg-content"
                          dangerouslySetInnerHTML={{ __html: cleanSvgForSplit }}
                          className="svg-inline-preview"
                          style={{
                            width: "100%",
                            borderRadius: "4px",
                            backgroundColor: "transparent",
                            lineHeight: 0,
                            overflow: "hidden",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                          }}
                        />
                      ) : (
                        <img
                          ref={svgRef}
                          data-tutorial="svg-content"
                          src={props.svgPreview}
                          alt="SVG Preview"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: "4px",
                            backgroundColor: "transparent",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                          }}
                        />
                      )}
                      {props.guestMode && <PreviewWatermark />}
                    </div>
                  )}
                </TransformComponent>
              )}
            </TransformWrapper>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#cbd5e1",
              fontSize: "13px",
              textAlign: "center",
              padding: "20px",
              fontFamily: "Clash Grotesk",
            }}
          >
            {props.file ? (
              <>
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    border: "3px solid #e2e8f0",
                    borderTop: "3px solid #e83e8c",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "14px",
                  }}
                />
                <div style={{ fontWeight: 500, color: "#64748b", marginBottom: "6px", fontSize: "14px" }}>
                  Converting to SVG...
                </div>
                <div style={{ fontSize: "11px", color: "#94a3b8", maxWidth: "220px", lineHeight: "1.4" }}>
                  Tracing colors and generating vector paths
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.6 }}>🧵</div>
                <div style={{ fontWeight: 500, color: "#94a3b8", marginBottom: "4px" }}>
                  SVG preview will appear here
                </div>
                <div style={{ fontSize: "11px", color: "#cbd5e1" }}>
                  Add colors to generate
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Centered zoom controls (shared between both halves) */}
      <div
        style={{
          position: "absolute",
          bottom: "16px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: "6px",
          zIndex: 10,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderRadius: "10px",
          padding: "4px 8px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
        }}
      >
        <Button shape="circle" icon={<ZoomIn size={14} style={{ display: "block" }} />} size="small"
          onClick={syncedZoomIn} title="Zoom in" style={zoomBtnStyle} />
        <Button shape="circle" icon={<ZoomOut size={14} style={{ display: "block" }} />} size="small"
          onClick={syncedZoomOut} title="Zoom out" style={zoomBtnStyle} />
        <Button shape="circle" icon={<Crosshair size={14} style={{ display: "block" }} />} size="small"
          onClick={syncedReset} title="Reset zoom" style={zoomBtnStyle} />
      </div>
    </div>

    {/* iOS paywall */}
    {showIOSPaywall && (
      <IOSPaywall
        standalone
        action="export"
        onClose={() => setShowIOSPaywall(false)}
        onSuccess={() => setShowIOSPaywall(false)}
      />
    )}
  </>
  );
};

export default SplitCanvas;
